import React, { useState, useEffect } from 'react';
import { quizAPI } from '../../utils/api';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import { QUIZ_DIFFICULTIES } from '../../utils/constants';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface Quiz {
  _id: string;
  title: string;
  description: string;
  difficulty: string;
  questions: QuizQuestion[];
  isActive: boolean;
  active?: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminQuizzes: React.FC = () => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [selectedQuiz, setSelectedQuiz] = useState<Quiz | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: QUIZ_DIFFICULTIES[0],
    isActive: false,
    questions: [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }],
  });

  const fetchQuizzes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await quizAPI.getAllQuizzes(1, 100, true);

      // Check if response.data.quizzes exists, otherwise use response.data
      const quizData = response.data.quizzes || response.data;
      
      // Ensure each quiz has a questions array and map active to isActive
      const processedQuizzes = Array.isArray(quizData) 
        ? quizData.map(quiz => ({
            ...quiz,
            questions: Array.isArray(quiz.questions) ? quiz.questions : [],
            isActive: quiz.active // Map active from API to isActive for UI
          }))
        : [];
      
      setQuizzes(processedQuizzes);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente quizzer');
      setQuizzes([]); // Initialize with empty array to prevent undefined errors
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleQuestionChange = (index: number, field: string, value: string) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value
      };
      return {
        ...prev,
        questions: updatedQuestions
      };
    });
  };

  const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.questions];
      const updatedOptions = [...updatedQuestions[questionIndex].options];
      updatedOptions[optionIndex] = value;
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        options: updatedOptions
      };
      return {
        ...prev,
        questions: updatedQuestions
      };
    });
  };

  const handleCorrectAnswerChange = (questionIndex: number, value: string) => {
    setFormData(prev => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        correctAnswer: parseInt(value, 10)
      };
      return {
        ...prev,
        questions: updatedQuestions
      };
    });
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        { question: '', options: ['', '', '', ''], correctAnswer: 0 }
      ]
    }));
  };

  const removeQuestion = (index: number) => {
    if (formData.questions.length <= 1) {
      return; // Don't remove the last question
    }
    
    setFormData(prev => {
      const updatedQuestions = [...prev.questions];
      updatedQuestions.splice(index, 1);
      return {
        ...prev,
        questions: updatedQuestions
      };
    });
  };

  const handleEditClick = (quiz: Quiz) => {
    setSelectedQuiz(quiz);
    setFormData({
      title: quiz.title,
      description: quiz.description,
      difficulty: quiz.difficulty,
      isActive: quiz.isActive,
      questions: Array.isArray(quiz.questions) 
        ? quiz.questions 
        : [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }],
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleCreateClick = () => {
    setSelectedQuiz(null);
    setFormData({
      title: '',
      description: '',
      difficulty: QUIZ_DIFFICULTIES[0],
      isActive: false,
      questions: [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }],
    });
    setIsEditing(false);
    setIsCreating(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Create a copy of formData with active instead of isActive for the API
      const apiData = {
        ...formData,
        active: formData.isActive, // Map isActive to active for the API
      };
      
      if (isEditing && selectedQuiz) {
        // Use quizAPI directly instead of the useApi hook
        await quizAPI.updateQuiz(selectedQuiz._id, apiData);
      } else if (isCreating) {
        // Use quizAPI directly instead of the useApi hook
        await quizAPI.createQuiz(apiData);
      }
      
      // Refresh the quizzes list
      await fetchQuizzes();
      
      // Reset form state
      setIsEditing(false);
      setIsCreating(false);
      setSelectedQuiz(null);
    } catch (err) {
      console.error('Failed to save quiz', err);
    }
  };

  const handleDeleteQuiz = async (id: string) => {
    if (!window.confirm('Er du sikker på, at du vil slette denne quiz?')) {
      return;
    }
    
    try {
      await quizAPI.deleteQuiz(id);
      
      // Refresh the quizzes list
      await fetchQuizzes();
      
      if (selectedQuiz?._id === id) {
        setSelectedQuiz(null);
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Failed to delete quiz', err);
    }
  };

  if (isLoading && quizzes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (error && quizzes.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (isEditing || isCreating) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">
            {isEditing ? 'Rediger quiz' : 'Opret ny quiz'}
          </h3>
          <Button variant="ghost" onClick={handleCancelEdit}>
            Annuller
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Titel
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              />
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Beskrivelse
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                  Sværhedsgrad
                </label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                >
                  {QUIZ_DIFFICULTIES.map((difficulty) => (
                    <option key={difficulty} value={difficulty}>
                      {difficulty}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Aktiv
                </label>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-lg font-medium text-gray-800 mb-4">Spørgsmål</h4>
            
            {formData.questions.map((question, questionIndex) => (
              <div key={questionIndex} className="mb-8 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <h5 className="font-medium">Spørgsmål {questionIndex + 1}</h5>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeQuestion(questionIndex)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Fjern
                  </Button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor={`question-${questionIndex}`} className="block text-sm font-medium text-gray-700 mb-1">
                      Spørgsmål
                    </label>
                    <input
                      type="text"
                      id={`question-${questionIndex}`}
                      value={question.question}
                      onChange={(e) => handleQuestionChange(questionIndex, 'question', e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                    />
                  </div>
                  
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-2">Svarmuligheder</p>
                    {question.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center mb-2">
                        <input
                          type="radio"
                          id={`correct-${questionIndex}-${optionIndex}`}
                          name={`correct-${questionIndex}`}
                          value={optionIndex}
                          checked={question.correctAnswer === optionIndex}
                          onChange={(e) => handleCorrectAnswerChange(questionIndex, e.target.value)}
                          className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300"
                        />
                        <input
                          type="text"
                          id={`option-${questionIndex}-${optionIndex}`}
                          value={option}
                          onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value)}
                          required
                          className="ml-2 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                          placeholder={`Svarmulighed ${optionIndex + 1}`}
                        />
                      </div>
                    ))}
                    <p className="text-sm text-gray-500 mt-1">
                      Vælg den korrekte svarmulighed ved at klikke på radioknappen.
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              onClick={addQuestion}
              className="w-full mt-4"
            >
              Tilføj spørgsmål
            </Button>
          </div>
          
          <div className="pt-4">
            <Button type="submit" className="w-full">
              {isEditing ? 'Gem ændringer' : 'Opret quiz'}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Quizzer</h2>
        <Button onClick={handleCreateClick}>Opret ny quiz</Button>
      </div>

      {quizzes.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">Ingen quizzer fundet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Mobile view - card layout */}
          <div className="md:hidden space-y-4 p-4">
            {quizzes.map((quiz) => (
              <div key={quiz._id} className="border rounded-lg p-4 space-y-3">
                <div className="font-medium text-lg">{quiz.title}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Sværhedsgrad:</span> {quiz.difficulty}
                  </div>
                  <div>
                    <span className="text-gray-600">Spørgsmål:</span> {quiz.questions?.length || 0}
                  </div>
                </div>
                <div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      quiz.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {quiz.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                <div className="flex space-x-2 pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(quiz)}
                    className="flex-1 justify-center"
                  >
                    Rediger
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-center text-red-600 hover:text-red-800 hover:bg-red-50"
                    onClick={() => handleDeleteQuiz(quiz._id)}
                  >
                    Slet
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop view - table layout */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Sværhedsgrad</TableHead>
                  <TableHead>Spørgsmål</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Handlinger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quizzes.map((quiz) => (
                  <TableRow key={quiz._id}>
                    <TableCell className="font-medium">{quiz.title}</TableCell>
                    <TableCell>{quiz.difficulty}</TableCell>
                    <TableCell>{quiz.questions?.length || 0}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          quiz.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {quiz.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(quiz)}
                        >
                          Rediger
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          onClick={() => handleDeleteQuiz(quiz._id)}
                        >
                          Slet
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuizzes; 
